import React from 'react';
import PropTypes from 'prop-types';
import './PlayoffLiveScoreboard.css';

const PlayoffLiveScoreboard = ({ liveScoreboard, homeTeamName, awayTeamName }) => {
  if (!liveScoreboard || !liveScoreboard.status) {
    return null;
  }

  const { status, period, clock, situation, possession, last_play, homeLineScores, awayLineScores, homeScore, awayScore } = liveScoreboard;

  // Determine status display
  const getStatusDisplay = () => {
    if (status === 'in_progress') {
      // Convert period to string if it's a number
      const periodDisplay = period ? (typeof period === 'number' ? `Q${period}` : period) : 'Q1';
      return `🟢 ${periodDisplay} ${clock ? `- ${clock}` : ''}`;
    } else if (status === 'completed') {
      return '✅ Final';
    } else if (status === 'scheduled') {
      return '⏰ Scheduled';
    }
    return status || 'Unknown';
  };

  // Pad line scores to 4 quarters
  const padLineScores = (scores) => {
    const padded = [...(scores || [])];
    while (padded.length < 4) {
      padded.push(0);
    }
    return padded.slice(0, 4);
  };

  const homeScores = padLineScores(homeLineScores);
  const awayScores = padLineScores(awayLineScores);

  return (
    <div className="playoff-live-scoreboard">
      <h3>📊 Live Game Scoreboard</h3>
      <div className="scoreboard-container">
        <table className="scoreboard-table">
          <thead>
            <tr>
              <th>Team</th>
              <th>Q1</th>
              <th>Q2</th>
              <th>Q3</th>
              <th>Q4</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="home-team-row">
              <td className="team-name">{homeTeamName || 'Home'}</td>
              {homeScores.map((score, index) => (
                <td key={`home-q${index + 1}`}>{score}</td>
              ))}
              <td className="total-score">{homeScore ?? '—'}</td>
            </tr>
            <tr className="away-team-row">
              <td className="team-name">{awayTeamName || 'Away'}</td>
              {awayScores.map((score, index) => (
                <td key={`away-q${index + 1}`}>{score}</td>
              ))}
              <td className="total-score">{awayScore ?? '—'}</td>
            </tr>
          </tbody>
        </table>
        
        <div className="game-status">
          <div className="status-badge">{getStatusDisplay()}</div>
          {situation && (
            <div className="game-info-item">
              <strong>Situation:</strong> {situation}
            </div>
          )}
          {possession && (
            <div className="game-info-item">
              <strong>Possession:</strong> {possession}
            </div>
          )}
          {last_play && (
            <div className="game-info-item last-play">
              <strong>Last Play:</strong> {last_play}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PlayoffLiveScoreboard.propTypes = {
  liveScoreboard: PropTypes.shape({
    status: PropTypes.string,
    period: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    clock: PropTypes.string,
    situation: PropTypes.string,
    possession: PropTypes.string,
    last_play: PropTypes.string,
    homeLineScores: PropTypes.arrayOf(PropTypes.number),
    awayLineScores: PropTypes.arrayOf(PropTypes.number),
    homeScore: PropTypes.number,
    awayScore: PropTypes.number,
  }),
  homeTeamName: PropTypes.string,
  awayTeamName: PropTypes.string,
};

export default PlayoffLiveScoreboard;
